import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/** Figma-aligned indigo pulse card shell */
export function AiCardShell({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50 p-5 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function AiInsightSkeleton({ lines = 3 }) {
  return (
    <div className="animate-pulse space-y-3" aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={`h-3 rounded-full bg-indigo-100/80 ${i === 0 ? 'w-3/4' : i === lines - 1 ? 'w-1/2' : 'w-full'}`}
        />
      ))}
    </div>
  );
}

export function AiSourceBadge({ source, cacheStatus, llmInvoked }) {
  let label = 'Rules';
  let className = 'bg-slate-100 text-slate-700';

  if (source === 'ai' || llmInvoked) {
    label = 'AI polished';
    className = 'bg-indigo-100 text-indigo-800';
  } else if (source === 'cache' || cacheStatus === 'fresh') {
    label = 'Cached';
    className = 'bg-emerald-50 text-emerald-800';
  }

  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${className}`}
      title="How this insight was produced">
      {label}
    </span>
  );
}

export function AiInsightError({ message, onRetry }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-900">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p>{message || 'Could not load AI insights.'}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-800 underline">
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Empty string / null / undefined → portfolio-wide (all projects). */
export function resolveAiProjectId(projectId) {
  if (projectId === null || projectId === undefined) return null;
  const raw = String(projectId).trim();
  if (!raw) return null;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
}

export function isAllProjectsAiScope(projectId) {
  return resolveAiProjectId(projectId) === null;
}

export function formatAiApiError(error) {
  const status = error?.response?.status;
  const msg = error?.response?.data?.message || error?.message || '';
  if (status === 429) {
    return 'Too many AI requests. Please wait a minute and try again.';
  }
  if (
    msg === 'API not found' ||
    status === 400 ||
    status === 404 ||
    msg.toLowerCase().includes('network error') ||
    error?.code === 'ERR_NETWORK'
  ) {
    return 'AI Insights API unreachable. Start housingram-be on port 3000 (or fix REACT_APP_API_BASE_URL), then restart the frontend.';
  }
  return msg || 'Could not load AI insights.';
}

export function formatAiCreditBalance(credits) {
  const n = Number(credits?.creditBalance ?? credits?.credit_balance ?? 0);
  if (!Number.isFinite(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.max(0, Math.floor(n)));
}

export function AiCreditsBadge({ aiCredits, className = '' }) {
  if (!aiCredits?.enforcementEnabled) return null;

  const balance = formatAiCreditBalance(aiCredits);
  const depleted = aiCredits.depleted;
  const low = aiCredits.lowBalance && !depleted;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        depleted
          ? 'bg-red-100 text-red-800'
          : low
            ? 'bg-amber-100 text-amber-900'
            : 'bg-teal-50 text-teal-800'
      } ${className}`}
      title={
        aiCredits.userMessage ||
        `Account AI credits: ${balance} remaining (rules-based insights still work without credits)`
      }>
      Account AI credits · {balance}
    </span>
  );
}

export function AiCreditsNotice({ aiCredits }) {
  if (!aiCredits?.enforcementEnabled || !aiCredits?.userMessage) return null;
  const isError = aiCredits.depleted || !aiCredits.llmEnabled;
  return (
    <div
      className={`mb-3 rounded-xl border px-3 py-2 text-xs leading-relaxed ${
        isError
          ? 'border-amber-300 bg-amber-50 text-amber-950'
          : 'border-teal-200 bg-teal-50/80 text-teal-900'
      }`}>
      <p>{aiCredits.userMessage}</p>
    </div>
  );
}
